/**
 * apps/web/src/router.tsx
 * -------------------------------------------------------
 * Purpose:
 *   React Router "data router" configuration (RR7-style).
 *
 * High-level data flow:
 *   - The root protected route runs `rootLoader()` which calls `/api/me`
 *     to check authentication. The sidebar count loads independently of navigation.
 *   - Child routes use loaders/actions to fetch/mutate data through the API.
 *   - All network calls go through `src/api/client.ts` which validates responses
 *     using shared zod schemas from `packages/contracts`.
 */

import React from 'react';
import {
  createBrowserRouter,
  redirect,
  type ActionFunctionArgs,
  type LoaderFunctionArgs,
} from 'react-router-dom';

import { AppLayout } from './components/AppLayout';
import { RouteErrorPage } from './components/RouteErrorPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { AuditsPage } from './pages/AuditsPage';
import { AuditViewerPage } from './pages/AuditViewerPage';
import { AdminAuditReviewStatesPage } from './pages/AdminAuditReviewStatesPage';
import { InboxPage } from './pages/UnifiedInboxPage';
import { LoginPage } from './pages/LoginPage';
import { BacktestsPage } from './pages/BacktestsPage';
import { PromptsPage } from './pages/PromptsPage';
import { PurchasingPolicyPage } from './pages/PurchasingPolicyPage';
import { ApiKeysPage } from './pages/ApiKeysPage';
import { ProjectAuditsPage } from './pages/ProjectAuditsPage';
import { ProjectAuditDetailPage } from './pages/ProjectAuditDetailPage';
import {
  ApiKeysPageSchema,
  ProjectsPageSchema,
  ProjectDetailSchema,
  ReviewQueueSchema,
  WorkflowPolicyViewSchema,
} from '@tinypro-audit/contracts';
import { fetchJson, ApiClientError } from './api/client';

import { api } from './api/client';
import { normalizeAnalyticsRange } from './pages/analyticsRanges';

/** Root loader that protects the app by requiring an authenticated session. */
async function rootLoader(): Promise<unknown> {
  try {
    return { me: await api.getMe() };
  } catch (err) {
    // If unauthenticated, redirect to login and preserve where we came from.
    if (err instanceof ApiClientError && err.status === 401) {
      throw redirect('/login');
    }
    throw err;
  }
}

/** Loader for the Analytics page. */
async function analyticsLoader({
  request,
}: LoaderFunctionArgs): Promise<unknown> {
  // Read and normalize a time range hint from the URL.
  const url = new URL(request.url);
  const range = normalizeAnalyticsRange(url.searchParams.get('range'));

  // Fetch summary + a small recent flagged list for quick drilldown.
  const [summary, recent] = await Promise.all([
    api.getAnalyticsSummary(range),
    api.listAudits({ filter: 'all', limit: 10, range }),
  ]);

  return { summary, recentAudits: recent.items, range };
}

/** The combined queue filters and groups both workflows before pagination. */
async function inboxLoader({ request }: LoaderFunctionArgs): Promise<unknown> {
  const query = new URL(request.url).searchParams;
  // Assignment is hidden in the UI. Old bookmarks must not apply hidden filters.
  if (query.get('view') === 'mine' || query.has('owner')) {
    if (query.get('view') === 'mine') query.set('view', 'all');
    query.delete('owner');
    query.delete('offset');
    const search = query.toString();
    return redirect(`/inbox${search ? `?${search}` : ''}`);
  }
  query.set('limit', '50');
  return fetchJson(
    `/api/projects/workflow/queue?${query}`,
    { method: 'GET' },
    ReviewQueueSchema,
  );
}

/** Loader for the Audits page (supports free-text search). */
async function auditsLoader({ request }: LoaderFunctionArgs): Promise<unknown> {
  const url = new URL(request.url);
  const q = url.searchParams.get('q') ?? '';
  const data = await api.listAudits({
    search: q || undefined,
    filter: 'all',
    limit: 200,
  });
  return { q, items: data.items };
}

/** Loader for the admin-only resolve activity page. */
async function adminAuditReviewStatesLoader({
  request,
}: LoaderFunctionArgs): Promise<unknown> {
  const url = new URL(request.url);
  const range = normalizeAnalyticsRange(url.searchParams.get('range'));
  const [report, summary] = await Promise.all([
    api.getAdminAuditReviewActivity(range),
    api.getAnalyticsSummary(range),
  ]);
  return { report, range, flaggedCount: summary.flaggedCount };
}

/** Loader for the Audit Viewer page. */
async function auditLoader({ params }: LoaderFunctionArgs): Promise<unknown> {
  const auditId = params.auditId;
  if (!auditId) throw new Response('Missing auditId', { status: 400 });
  const detail = await api.getAudit(auditId);
  return { detail };
}

/** Action for the Login route (posts credentials to Better Auth). */
async function loginAction({ request }: ActionFunctionArgs): Promise<Response> {
  const formData = await request.formData();
  const email = String(formData.get('email') ?? '');
  const password = String(formData.get('password') ?? '');

  try {
    // Attempt sign-in; Better Auth sets the session cookie if successful.
    await api.signInEmail(email, password);
  } catch (err) {
    // Return a user-friendly message to be displayed on the login form.
    return Response.json(
      { error: err instanceof Error ? err.message : 'Login failed' },
      { status: 400 },
    );
  }

  // Redirect to the app root after login.
  return redirect('/');
}

/** Resource action route used for logging out (invalidates the session server-side). */
async function logoutAction(): Promise<Response> {
  await api.signOut();
  return redirect('/login');
}

/** Browser router instance used by <RouterProvider/>. */
export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
    action: loginAction,
    errorElement: <RouteErrorPage />,
  },
  {
    // Resource route: no UI, just an action.
    path: '/logout',
    action: logoutAction,
  },
  {
    id: 'root',
    path: '/',
    loader: rootLoader,
    element: <AppLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        path: 'admin/api-keys',
        element: <ApiKeysPage />,
        loader: () =>
          fetchJson(
            '/api/admin/api-keys',
            { method: 'GET' },
            ApiKeysPageSchema,
          ),
      },
      {
        path: 'ordering-inbox',
        loader: ({ request }) =>
          redirect(`/inbox${new URL(request.url).search}`),
      },
      {
        path: 'admin/review-policy',
        element: <PurchasingPolicyPage />,
        loader: () =>
          fetchJson(
            '/api/projects/workflow/policy',
            { method: 'GET' },
            WorkflowPolicyViewSchema,
          ),
      },
      {
        path: 'projects',
        element: <ProjectAuditsPage />,
        loader: ({ request }) =>
          fetchJson(
            `/api/projects${new URL(request.url).search}`,
            { method: 'GET' },
            ProjectsPageSchema,
          ),
      },
      {
        path: 'projects/pre-site',
        element: <AuditsPage />,
        loader: auditsLoader,
      },
      {
        path: 'projects/:projectId',
        element: <ProjectAuditDetailPage />,
        loader: ({ params }) =>
          fetchJson(
            `/api/projects/${encodeURIComponent(params.projectId ?? '')}`,
            { method: 'GET' },
            ProjectDetailSchema,
          ),
      },
      {
        index: true,
        element: <AnalyticsPage />,
        loader: analyticsLoader,
      },
      {
        path: 'inbox',
        element: <InboxPage />,
        loader: inboxLoader,
      },
      {
        path: 'audits',
        loader: ({ request }) =>
          redirect(`/projects/pre-site${new URL(request.url).search}`),
      },
      {
        path: 'audits/:auditId',
        element: <AuditViewerPage />,
        loader: auditLoader,
      },
      {
        path: 'admin/resolve-activity',
        element: <AdminAuditReviewStatesPage />,
        loader: adminAuditReviewStatesLoader,
      },
      {
        path: 'prompts',
        element: <PromptsPage />,
      },
      {
        path: 'backtests',
        element: <BacktestsPage />,
      },
    ],
  },
]);
